import { FlowState } from '@de/providers/dynamicExperienceReducer';
import { DynamicOptions, Option, Step } from '@de/types/types';
import { JSONPath } from 'jsonpath-plus';
import {
  fetchWithDynamicParams,
  JsonData,
  extractJsonPathValues,
} from './dynamicFetchUtils';

const mapResponseToOptions = (
  data: JsonData,
  dynamicOptions: DynamicOptions,
): Option[] => {
  const { limit, optionConfig } = dynamicOptions;

  if (!optionConfig) {
    console.warn('No optionConfig provided, returning empty options array');
    return [];
  }

  const {
    optionId: optionIdPath,
    text: textPath,
    value: valuePath,
    extractKeys,
  } = optionConfig;

  if (extractKeys) {
    // Extract the object at the optionIdPath and use its keys as options
    const targetObject = JSONPath({
      path: optionIdPath,
      json: data,
      wrap: false,
    });

    if (
      targetObject &&
      typeof targetObject === 'object' &&
      !Array.isArray(targetObject)
    ) {
      const keys = Object.keys(targetObject);

      // Apply limit if specified
      const finalKeys = limit ? keys.slice(0, limit) : keys;

      return finalKeys.map((key) => ({
        optionId: key,
        text: key,
        value: key,
      }));
    } else {
      console.warn(
        'extractKeys is true but optionIdPath did not resolve to an object:',
        targetObject,
      );
      return [];
    }
  }

  // Extract data using JSONPath expressions
  const optionIds = extractJsonPathValues(optionIdPath, data);
  const texts = extractJsonPathValues(textPath, data);
  const values = extractJsonPathValues(valuePath, data);

  // Determine the length based on the shortest array to avoid index out of bounds
  const length = Math.min(optionIds.length, texts.length, values.length);

  // Apply limit if specified
  const finalLength = limit ? Math.min(length, limit) : length;

  // Map the extracted data to Option objects
  return Array.from({ length: finalLength }, (_, index) => ({
    optionId: String(optionIds[index]),
    text: String(texts[index]),
    value: String(values[index]),
  }));
};

export const fetchDynamicOptions = async (
  dynamicOptions: DynamicOptions,
  flowState: FlowState,
  steps: Step[],
): Promise<Option[]> => {
  return fetchWithDynamicParams(
    { dynamicContent: dynamicOptions, flowState, steps },
    'dynamic options',
    mapResponseToOptions,
  );
};
